import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Moon } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function RestSleepPublic() {
  const [board, setBoard] = useState(null);
  const [wb, setWb] = useState(null);
  const [category, setCategory] = useState("");

  useEffect(() => {
    api.get("/wellbeing/public").then(({ data }) => setWb(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/rest-sleep/public", { params: category ? { category } : {} })
      .then(({ data }) => setBoard(data))
      .catch(() => setBoard({ products: [], categories: [], rooms: [] }));
  }, [category]);

  useSeo({
    title: "Rest & sleep | Unity & Utility",
    description: (board?.blurb || "Quiet rooms and nap pods on site, and the rest and recovery lines we sell — mattresses, pods, eye masks and blackout blinds.").slice(0, 180),
    path: "/rest-sleep-board",
    image: board?.picture ? `${BACKEND}${board.picture}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Rest & sleep", "/rest-sleep-board"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="rest-public-page">
        <Crumbs items={[["Home", "/"], ["Rest & sleep", "/rest-sleep-board"]]} />
        <p className="overline text-[#B3241B]">Rest well, work well</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Moon size={36} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          Rest &amp; sleep
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="rest-public-blurb">
          {board?.blurb || "Quiet rooms and nap pods on site, and the rest and recovery lines we sell for the journey home and the night after it."}
        </p>
        <div className="mt-5">
          <ShareBar url={`${BACKEND}/api/share/rest-sleep`} text="Rest & sleep"
            testid="rest-public-share" instagram />
        </div>

        {board?.poster && (
          <img data-testid="rest-public-poster" src={`${BACKEND}${board.poster}`}
            alt="Today's rest and sleep board"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-72" />
        )}

        {board?.campaign?.headline && (
          <div className="mt-8 border border-border bg-white p-4" data-testid="rest-public-campaign">
            <p className="overline text-[#B3241B]">Stop the scroll</p>
            <p className="mt-1 text-base font-semibold text-[#0F172A]">{board.campaign.headline}</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-700">{board.campaign.message}</p>
            <p className="mt-2 text-xs font-semibold text-[#002FA7]">{(board.campaign.hashtags || []).join(" ")}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link to="/rest-sleep-campaign" data-testid="rest-public-campaign-link"
                className="tap-safe text-sm font-semibold text-[#002FA7] hover:underline">
                Open today's message
              </Link>
              <ShareBar url={`${BACKEND}/api/share/rest-sleep-campaign`} text={board.campaign.headline}
                testid="rest-public-campaign-share" compact instagram />
            </div>
          </div>
        )}

        {board?.rules?.rules?.length > 0 && (
          <div className="mt-8 border-l-2 border-[#B3241B] bg-[#FFF5F4] p-4" data-testid="rest-public-rules">
            <p className="overline text-[#B3241B]">No phone, no screen</p>
            <p className="mt-1 text-base font-semibold text-[#0F172A]">{board.rules.line}</p>
            <ul className="mt-2 grid gap-1 text-sm text-slate-700 sm:grid-cols-2">
              {board.rules.rules.map((r, i) => <li key={i}>· {r}</li>)}
            </ul>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link to="/rest-sleep-rule" data-testid="rest-public-rule-link"
                className="tap-safe text-sm font-semibold text-[#002FA7] hover:underline">
                Open the rule card
              </Link>
              <ShareBar url={`${BACKEND}/api/share/rest-sleep-rule`} text={board.rules.line || "No phone, no screen"}
                testid="rest-public-rule-share" compact instagram />
            </div>
          </div>
        )}

        {board?.rooms?.length > 0 && (
          <div className="mt-8 border border-border bg-white p-4" data-testid="rest-public-rooms">
            <p className="text-sm font-semibold text-[#0F172A]">Quiet rooms on site</p>
            <ul className="mt-2 grid gap-1 text-sm text-slate-700 sm:grid-cols-2">
              {board.rooms.map((r, i) => (
                <li key={i}>· {r.name} — {LABEL(r.kind)}{r.site ? `, ${r.site}` : ""} · sleeps {r.capacity}</li>
              ))}
            </ul>
          </div>
        )}

        {wb && (
          <div className="mt-10" data-testid="rest-public-breaks">
            <h2 className="text-base font-semibold text-[#0F172A] md:text-lg">Breaks in the working day</h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-700" data-testid="rest-public-breaks-blurb">
              {wb.blurb}
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {(wb.breaks || []).map((b) => (
                <div key={b.key} data-testid={`rest-public-break-${b.key}`} className="border border-border bg-white">
                  {b.image_url && (
                    <img src={`${BACKEND}${b.image_url}`} alt={b.label} className="h-32 w-full object-cover" />
                  )}
                  <div className="p-3">
                    <p className="font-semibold text-[#0F172A]">{b.label}</p>
                    <p className="text-sm text-slate-600">{b.minutes} minutes</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-sm text-slate-600" data-testid="rest-public-water">
              Water target: {wb.water_target} glasses a day.
            </p>

            <h2 className="mt-8 text-base font-semibold text-[#0F172A] md:text-lg">Sleep therapy plans</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="rest-public-plans">
              {(wb.plans || []).map((p, i) => (
                <div key={i} data-testid={`rest-public-plan-${i}`} className="border border-border bg-white p-4">
                  <p className="font-semibold text-[#0F172A]">{p.name}</p>
                  <p className="mt-1 text-xl font-bold text-[#0B5C2E]">{p.currency} {p.price}
                    <span className="text-xs font-semibold text-slate-500"> / month</span></p>
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {(p.includes || []).map((x, n) => <li key={n}>· {x}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link to="/wellbeing" data-testid="rest-public-wellbeing-link"
                className="tap-safe text-sm font-semibold text-[#002FA7] hover:underline">
                Open the wellbeing board
              </Link>
              <ShareBar url={`${BACKEND}/api/share/wellbeing`} text="Breaks, naps and sleep therapy at work"
                testid="rest-public-wellbeing-share" compact instagram />
            </div>
          </div>
        )}

        {board?.categories?.length > 0 && (
          <div className="scroll-x mt-8 flex gap-1.5 overflow-x-auto" data-testid="rest-public-filter">
            {[["", "Everything"], ...board.categories.map((c) => [c, LABEL(c)])].map(([key, label]) => (
              <button key={key || "all"} data-testid={`rest-public-category-${key || "all"}`} onClick={() => setCategory(key)}
                className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  category === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {board?.products?.length === 0 ? (
          <p className="mt-8 text-sm text-slate-500" data-testid="rest-public-empty">
            Nothing on the rest board for that filter just now.
          </p>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="rest-public-list">
            {board?.products?.map((p, i) => (
              <div key={p.id} data-testid={`rest-public-${i}`} className="border border-border bg-white">
                {p.image_url && (
                  <Link to={`/rest-sleep-board/${p.id}`}>
                    <img src={`${BACKEND}${p.image_url}`} alt={p.name} className="h-44 w-full object-cover" />
                  </Link>
                )}
                <div className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">{LABEL(p.category)}</p>
                  <Link to={`/rest-sleep-board/${p.id}`} className="mt-1 block font-semibold text-[#0F172A] hover:text-[#002FA7]">
                    {p.name}
                  </Link>
                  <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{p.symbol}{p.price}</p>
                  <div className="mt-2">
                    <ShareBar url={`${BACKEND}/api/share/rest-sleep/${p.id}`} text={p.name}
                      testid={`rest-public-share-${i}`} compact instagram />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

export function RestCampaignPublic() {
  const [c, setC] = useState(null);

  useEffect(() => {
    api.get("/rest-sleep/campaign").then(({ data }) => setC(data)).catch(() => setC({}));
  }, []);

  useSeo({
    title: `${c?.headline || "Stop the scroll, take the sleep"} | Unity & Utility`,
    description: (c?.message || "Scrolling after lights out costs you the hours you need.").slice(0, 180),
    path: "/rest-sleep-campaign",
    image: c?.image_url ? `${BACKEND}${c.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Rest & sleep", "/rest-sleep-board"], ["Stop the scroll", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="rest-campaign-page">
        <Crumbs items={[["Home", "/"], ["Rest & sleep", "/rest-sleep-board"], ["Stop the scroll", null]]} />
        <p className="overline text-[#B3241B]">Sleep campaign{c?.day ? ` · ${c.day}` : ""}</p>
        <h1 data-testid="rest-campaign-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
          {c?.headline || "Stop the scroll, take the sleep"}
        </h1>
        {c?.image_url && (
          <img data-testid="rest-campaign-image" src={`${BACKEND}${c.image_url}`} alt={c.headline}
            className="mt-6 w-full border border-border object-cover" />
        )}
        <p data-testid="rest-campaign-message" className="mt-5 whitespace-pre-line text-base leading-relaxed text-slate-700">
          {c?.message}
        </p>
        <p className="mt-3 text-sm font-semibold text-[#002FA7]" data-testid="rest-campaign-hashtags">
          {(c?.hashtags || []).join(" ")}
        </p>
        <div className="mt-6">
          <ShareBar url={`${BACKEND}/api/share/rest-sleep-campaign`}
            text={`${c?.headline || "Stop the scroll"} ${(c?.hashtags || []).join(" ")}`}
            testid="rest-campaign-share" instagram />
        </div>
        <Link to="/rest-sleep-board" data-testid="rest-campaign-back"
          className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
          Back to the rest board
        </Link>
      </div>
    </PublicShell>
  );
}

export function RestRulePublic() {
  const [rules, setRules] = useState(null);

  useEffect(() => {
    api.get("/rest-sleep/rules").then(({ data }) => setRules(data)).catch(() => setRules({ rules: [] }));
  }, []);

  useSeo({
    title: "No phone, no screen — our quiet room rule | Unity & Utility",
    description: (rules?.line || "In our quiet rooms the phone and the laptop stay away, and screens go off before sleep.").slice(0, 180),
    path: "/rest-sleep-rule",
    image: rules?.image_url ? `${BACKEND}${rules.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Rest & sleep", "/rest-sleep-board"], ["The rule", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="rest-rule-page">
        <Crumbs items={[["Home", "/"], ["Rest & sleep", "/rest-sleep-board"], ["The rule", null]]} />
        <p className="overline text-[#B3241B]">House rule</p>
        <h1 data-testid="rest-rule-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
          No phone, no screen
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-700" data-testid="rest-rule-line">
          {rules?.line || "In our quiet rooms the phone and the laptop stay away."}
        </p>
        {rules?.image_url && (
          <img data-testid="rest-rule-image" src={`${BACKEND}${rules.image_url}`} alt="Phone away, laptop closed"
            className="mt-6 w-full border border-border object-cover" />
        )}
        <ul className="mt-6 space-y-2 text-base text-slate-800" data-testid="rest-rule-list">
          {(rules?.rules || []).map((r, i) => (
            <li key={i} className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2">{r}</li>
          ))}
        </ul>
        <p className="mt-6 text-sm text-slate-600" data-testid="rest-rule-alarm-note">
          The one exception: an emergency. If the site raises an alarm, an AI wake-up message reaches
          everyone resting with what has happened and where to go — so the phone can stay away.
        </p>
        <div className="mt-6">
          <ShareBar url={`${BACKEND}/api/share/rest-sleep-rule`} text={rules?.line || "No phone, no screen"}
            testid="rest-rule-share" instagram />
        </div>
        <Link to="/rest-sleep-board" data-testid="rest-rule-back"
          className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
          Back to the rest board
        </Link>
      </div>
    </PublicShell>
  );
}

export function RestItemPublic() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/rest-sleep/public/${id}`)
      .then(({ data }) => setItem(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Not on the rest board"));
  }, [id]);

  useSeo({
    title: item ? `${item.name} | Rest & sleep | Unity & Utility` : "Rest & sleep | Unity & Utility",
    description: (item?.description || item?.name || "From our rest and recovery range.").slice(0, 180),
    path: `/rest-sleep-board/${id}`,
    image: item?.image_url ? `${BACKEND}${item.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Rest & sleep", "/rest-sleep-board"], [item?.name || "Line", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="rest-item-page">
        <Crumbs items={[["Home", "/"], ["Rest & sleep", "/rest-sleep-board"], [item?.name || "Line", null]]} />
        {error && <p data-testid="rest-item-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {item && (
          <>
            <p className="overline text-[#B3241B]">{LABEL(item.category)}</p>
            <h1 data-testid="rest-item-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              {item.name}
            </h1>
            {item.image_url && (
              <img data-testid="rest-item-image" src={`${BACKEND}${item.image_url}`} alt={item.name}
                className="mt-6 w-full border border-border object-cover" />
            )}
            <p data-testid="rest-item-price" className="mt-5 text-2xl font-bold text-[#0B5C2E]">
              {item.symbol}{item.price}
              {item.quantity != null && (
                <span className="ml-2 align-middle text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {item.quantity} available{item.seller ? ` · ${item.seller}` : ""}
                </span>
              )}
            </p>
            {item.description && (
              <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{item.description}</p>
            )}
            <div className="mt-6">
              <ShareBar url={`${BACKEND}/api/share/rest-sleep/${id}`} text={item.name}
                testid="rest-item-share" instagram />
            </div>
            <Link to="/rest-sleep-board" data-testid="rest-item-back"
              className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
              See the whole rest board
            </Link>
          </>
        )}
      </div>
    </PublicShell>
  );
}
